import { LRUCache } from '../../lib/lru-cache';
import { splitGraphemes, WrapType } from './wrap-text';

/**
 * Get the last text index from a text layout
 * @param {Object} textLayout - The text layout object from TextLayoutProvider
 * @returns {number} The last text index
 */
export function getLastTextIndex(textLayout) {
  if (!textLayout || !textLayout.lines.length) return 0;

  const { lines } = textLayout;

  return positionToTextIndex(textLayout, [lines.length - 1, lines[lines.length - 1].length]);
}

/**
 * Convert a position [line, column] to text index
 * @param {Object} textLayout - The text layout object from TextLayoutProvider
 * @param {[number, number]} position - The position as [line, column]
 * @returns {number} The text index
 */
export function positionToTextIndex(textLayout, position) {
  if (!textLayout || !textLayout.lines.length) return 0;

  const { lines, linesBreakType } = textLayout;

  let index = 0;
  for (let i = 0; i < lines.length; ++i) {
    const line = lines[i];
    if (i > 0 && linesBreakType[i] === WrapType.HARD) {
      index += 1;
    }

    if (i < position[0]) {
      index += line.length;
    } else if (i === position[0]) {
      index += Math.min(position[1], line.length);
      break;
    }
  }

  return index;
}

export function drawParallelogram(ctx, rect, skewX, radius) {
  const { x, y, w, h } = rect;

  ctx.beginPath();
  // 右上角
  ctx.moveTo(x + w - radius, y);
  ctx.arcTo(x + w, y, x + w + skewX, y + h, radius);

  // 右下角
  ctx.lineTo(x + w + skewX - radius, y + h);
  ctx.arcTo(x + w + skewX, y + h, x + skewX, y + h, radius);

  // 左下角
  ctx.lineTo(x + skewX + radius, y + h);
  ctx.arcTo(x + skewX, y + h, x, y, radius);

  // 左上角
  ctx.lineTo(x + radius, y);
  ctx.arcTo(x, y, x + w, y, radius);

  ctx.closePath();
  ctx.fill();
}

export function drawHandDrawnCircle(ctx, cx, cy, w, lineHeight, fontSize, highlight) {
  const random = new Math.seedrandom(1000);
  const points = [];
  const numPoints = highlight.point ?? 26;
  const padding = (highlight.padding ?? 0.2) * fontSize;
  const size = (highlight.size ?? 0.2) * fontSize;

  cx = cx + w / 2;
  const ellipseWidth = w + padding * 4;
  const ellipseHeight = lineHeight + padding;
  for (let i = 0; i < numPoints; i++) {
    const angle = (i / numPoints) * 2 * Math.PI;
    const rX = ellipseWidth / 2 + (random() * 10 - 5); // Random radius variation for x-axis
    const rY = ellipseHeight / 2 + (random() * 10 - 5); // Random radius variation for y-axis
    const x = cx + rX * Math.cos(angle);
    const y = cy + rY * Math.sin(angle);
    points.push({ x, y });
  }
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i].x, points[i].y);
  }
  ctx.closePath();
  ctx.strokeStyle = highlight.fill;
  ctx.lineWidth = size;
  ctx.stroke();
}

export const FONT_FAMILY = {
  Virgil: 1,
  Helvetica: 2,
  Cascadia: 3,
  Assistant: 4,
};

const DEFAULT_LINE_HEIGHT = {
  // ~1.25 is the average for Virgil in WebKit and Blink.
  // Gecko (FF) uses ~1.28.
  [FONT_FAMILY.Virgil]: 1.25,
  // ~1.15 is the average for Helvetica in WebKit and Blink.
  [FONT_FAMILY.Helvetica]: 1.15,
  // ~1.2 is the average for Cascadia in WebKit and Blink, and kinda Gecko too
  [FONT_FAMILY.Cascadia]: 1.2,
};

export const getDefaultLineHeight = (fontFamily) => {
  if (fontFamily in DEFAULT_LINE_HEIGHT) {
    return DEFAULT_LINE_HEIGHT[fontFamily];
  }
};

export const normalizeEOL = (str) => {
  return str.replace(/\r?\n|\r/g, '\n');
};

export const getTextWidth = (text, font, letterSpacing) => {
  const lines = splitIntoLines(text);
  let width = 0;
  lines.forEach((line) => {
    width = Math.max(width, charWidth.calculateText(line, font, letterSpacing));
  });
  return width;
};

let canvas;
let spanElement;

// Text measurement mode: 'canvas' or 'span'
let measurementMode = 'canvas';

export const setTextMeasurementMode = (mode) => {
  if (mode === 'canvas' || mode === 'span') {
    measurementMode = mode;
  }
};

export const getTextMeasurementMode = () => measurementMode;

const getLineWidthCanvas = (text, font, letterSpacing = 0) => {
  if (!canvas) {
    canvas = document.createElement('canvas');
  }
  const canvas2dContext = canvas.getContext('2d');
  canvas2dContext.font = font;

  let width;
  const debug = false;

  // Safari and Chrome have different text measurement behaviors
  // Always use manual calculation for letterSpacing to ensure consistency
  letterSpacing = letterSpacing || 0;
  const spacing = letterSpacing;

  let list;
  if (debug) list = [];
  width = 0;
  const chars = splitGraphemes(text);
  for (let i = 0; i < chars.length; i++) {
    const char = chars[i];
    const metrics = canvas2dContext.measureText(char);

    // For Noto Sans font, use advanceWidth for better consistency with textarea
    // as it provides more accurate measurement for symbols and special characters
    // that may have inconsistent widths in Noto Sans
    // const charWidth = metrics.actualBoundingBoxRight + metrics.actualBoundingBoxLeft
    const charWidth = Math.floor(metrics.width);

    width += charWidth + (i < text.length - 1 ? spacing : 0);
    if (debug) {
      list.push({
        char,
        charWidth,
        spacing: i < text.length - 1 ? spacing : 0,
      });
    }
  }

  if (debug) {
    console.log('debug canvas', font, list);
  }

  return width;
};

const getLineWidthSpan = (text, font, letterSpacing = 0) => {
  if (!spanElement) {
    spanElement = document.createElement('span');
    spanElement.style.position = 'absolute';
    spanElement.style.left = '-9999px';
    spanElement.style.top = '-9999px';
    spanElement.style.padding = '0';
    spanElement.style.border = 'none';
    spanElement.style.whiteSpace = 'pre';
    spanElement.style.margin = '0';
    spanElement.style.lineHeight = '1';
    spanElement.style.whiteSpace = 'nowrap';
    spanElement.style.visibility = 'hidden';
  }

  const debug = false;

  // Parse font string to extract fontSize and fontFamily
  const fontMatch = font.match(/(\d+(?:\.\d+)?)px\s+(.+)/);
  if (fontMatch) {
    const [, fontSize, fontFamily] = fontMatch;
    spanElement.style.fontSize = `${fontSize}px`;
    spanElement.style.fontFamily = fontFamily;
  } else {
    spanElement.style.font = font;
  }

  if (letterSpacing) {
    spanElement.style.letterSpacing = `${letterSpacing}px`;
  } else {
    spanElement.style.letterSpacing = 'normal';
  }

  spanElement.textContent = text;

  // Temporarily add to DOM for measurement
  const wasInDocument = spanElement.parentNode;
  if (!wasInDocument) {
    document.body.appendChild(spanElement);
  }

  const width = spanElement.getBoundingClientRect().width;

  // Clean up if we added it
  if (!wasInDocument && spanElement.parentNode) {
    document.body.removeChild(spanElement);
  }

  if (debug) {
    // Get character breakdown for debugging
    const list = [];
    const chars = [...text];

    for (let i = 0; i < chars.length; i++) {
      const char = chars[i];

      // Measure individual character
      spanElement.textContent = char;
      if (!wasInDocument) {
        document.body.appendChild(spanElement);
      }
      const charWidth = spanElement.getBoundingClientRect().width;
      if (!wasInDocument && spanElement.parentNode) {
        document.body.removeChild(spanElement);
      }

      const spacing = i < chars.length - 1 ? letterSpacing || 0 : 0;

      list.push({
        char,
        charWidth,
        spacing,
      });
    }

    console.log('debug span', font, list);
  }

  return width;
};

const getLineWidth = (text, font, letterSpacing = 0) => {
  if (measurementMode === 'span') {
    return getLineWidthSpan(text, font, letterSpacing);
  } else {
    return getLineWidthCanvas(text, font, letterSpacing);
  }
};

export const normalizeText = (text) => {
  return (
    normalizeEOL(text)
      // replace tabs with spaces so they render and measure correctly
      .replace(/\t/g, '        ')
  );
};

const splitIntoLines = (text) => {
  return normalizeText(text).split('\n');
};

export const getLineHeightInPx = (fontSize, lineHeight) => {
  return fontSize * lineHeight;
};

export const getTextHeight = (text, fontSize, lineHeight) => {
  const lineCount = splitIntoLines(text).length;
  return getLineHeightInPx(fontSize, lineHeight) * lineCount;
};

export function copy(data) {
  return JSON.parse(JSON.stringify(data));
}

export function colorDarken(color, percent) {
  const num = parseInt(color.slice(1), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) - amt;
  const B = ((num >> 8) & 0x00ff) - amt;
  const G = (num & 0x0000ff) - amt;
  return `#${(0x1000000 + (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 + (B < 255 ? (B < 1 ? 0 : B) : 255) * 0x100 + (G < 255 ? (G < 1 ? 0 : G) : 255)).toString(16).slice(1)}`;
}

export const measureText = (text, font, lineHeight, letterSpacing) => {
  const result = _measureText(text, font, lineHeight, letterSpacing);
  return result;
};

export function htmlToText(html) {
  const el = document.createElement('div');
  el.innerHTML = html;
  return el.innerText;
}

export function preLayout(
  content,
  { width = 200, className = 'text-base', wrapper = '', style, remove = true }
) {
  const el = document.createElement('div');
  if (!wrapper) {
    el.innerHTML = content;
  } else {
    el.innerHTML = `<${wrapper}>${content}</${wrapper}>`;
  }
  if (className) {
    if (Array.isArray(className)) {
      className.forEach((c) => el.classList.add(c));
    } else {
      el.classList.add(className);
    }
  }
  el.style.visibility = 'hidden';
  el.style.width = `${width}px`;
  el.style.position = 'absolute';
  if (style) {
    Object.keys(style).forEach((key) => {
      el.style[key] = style[key];
    });
  }
  document.body.appendChild(el);
  const h = el.offsetHeight;
  const w = el.offsetWidth;
  if (remove) {
    document.body.removeChild(el);
  }
  return { height: h, width: w };
}

export const _measureText = (text, font, lineHeight, letterSpacing = 0) => {
  // Ensure text is a string by joining array elements with newlines if it's an array
  if (Array.isArray(text)) {
    text = text.join('\n');
  }

  text = text
    .split('\n')
    // replace empty lines with single space because leading/trailing empty
    // lines would be stripped from computation
    .map((x) => x || ' ')
    .join('\n');

  const result = parseFontString(font);
  const fontSize = result.fontSize;
  const height = getTextHeight(text, fontSize, lineHeight);
  const width = getTextWidth(text, font, letterSpacing);
  return { width, height };
};

export function parseFontString(font) {
  ///  'italic bold 96px "Orelega One"'
  /// parseFontString('italic bold 96px "Orelega One"')
  const result = font.split('"').filter(Boolean);
  const fontFamily = result[1];

  const list = result[0].split(' ').filter(Boolean);
  if (list.length === 1) {
    const [size] = list;
    return { fontSize: parseFloat(size), fontFamily: fontFamily };
  } else if (list.length === 2) {
    const [, size] = list;
    return { fontSize: parseFloat(size), fontFamily: fontFamily };
  } else if (list.length === 3) {
    const [, , size] = list;
    return { fontSize: parseFloat(size), fontFamily: fontFamily };
  } else {
    return {
      fontSize: parseFloat(font) || 10,
      fontFamily,
    };
  }
}

export function randomColor() {
  return '#' + Math.random().toString(16).substr(-6);
}

export function disableDefaultZoom() {
  window.addEventListener(
    'mousewheel',
    function (event) {
      if (event.ctrlKey === true || event.metaKey) {
        event.preventDefault();
      }
    },
    { passive: false }
  );
  window.addEventListener(
    'wheel',
    function (event) {
      if (event.ctrlKey === true || event.metaKey) {
        event.preventDefault();
      }
    },
    { passive: false }
  );

  //firefox
  window.addEventListener(
    'DOMMouseScroll',
    function (event) {
      if (event.ctrlKey === true || event.metaKey) {
        event.preventDefault();
      }
    },
    { passive: false }
  );

  document.addEventListener(
    'touchmove',
    function (event) {
      if (event.scale !== 1) {
        event.preventDefault();
      }
    },
    { passive: false }
  );
}

export function bytesToSize(bytes) {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Byte';
  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)), 10);
  if (i === 0) return `${bytes} ${sizes[i]}`;
  return `${(bytes / 1024 ** i).toFixed(1)} ${sizes[i]}`;
}

export function loadImage(imageUrl, onprogress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let notifiedNotComputable = false;

    xhr.open('GET', imageUrl, true);
    xhr.responseType = 'arraybuffer';

    xhr.onprogress = function (ev) {
      if (ev.lengthComputable) {
        onprogress(parseInt((ev.loaded / ev.total) * 100));
      } else {
        if (!notifiedNotComputable) {
          notifiedNotComputable = true;
          onprogress(-1);
        }
      }
    };

    xhr.onloadend = function () {
      if (!xhr.status.toString().match(/^2/)) {
        reject(xhr);

        return;
      }

      const options = {};
      const headers = xhr.getAllResponseHeaders();
      const m = headers.match(/^Content-Type:\s*(.*?)$/im);

      if (m && m[1]) {
        options.type = m[1];
      }

      resolve(window.URL.createObjectURL(new Blob([this.response], options)));
    };

    xhr.send();
  });
}

function hexToRGB(hex) {
  // Remove the # character from the beginning of the hex code
  hex = hex.replace('#', '');

  // Convert the red, green, and blue components from hex to decimal
  // you can substring instead of slice as well
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  // Return the RGB value as an object with properties r, g, and b
  return { r, g, b };
}

export const closestColor = (targetColor, colorArray) => {
  let closestDistance = null;
  let closestColor = null;

  // Convert target color from hex string to RGB values
  const [r1, g1, b1] = hexToRGB(targetColor);

  // Loop through the array of colors
  colorArray.forEach((color) => {
    // Convert current color from hex string to RGB values
    const [r2, g2, b2] = hexToRGB(color);

    // Calculate the Euclidean distance between the target color and current color
    const distance = Math.sqrt((r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2);

    // Update closest color and distance if the current distance is smaller than the closest distance
    if (distance < closestDistance) {
      closestDistance = distance;
      closestColor = color;
    }
  });

  return closestColor;
};

// Parse font string to extract components for better cache key
function parseFontKey(font) {
  // Format: "italic bold 16px \"Font Name\""
  const parts = font.split('"').filter(Boolean);
  const fontFamily = parts[1] || '';
  const stylePart = parts[0] || '';

  // Extract fontSize
  const fontSizeMatch = stylePart.match(/(\d+(?:\.\d+)?)px/);
  const fontSize = fontSizeMatch ? fontSizeMatch[1] : '16';

  // Extract fontWeight and fontStyle
  const styleWords = stylePart.split(/\s+/);
  let fontWeight = '';
  let fontStyle = '';

  for (const word of styleWords) {
    if (/^(normal|bold|bolder|lighter|\d+)$/.test(word)) {
      fontWeight = word;
    } else if (/^(normal|italic|oblique)$/.test(word)) {
      fontStyle = word;
    }
  }

  return {
    fontFamily: fontFamily.trim(),
    fontSize,
    fontWeight: fontWeight || 'normal',
    fontStyle: fontStyle || 'normal',
  };
}

// Create optimized cache key from font components
function createFontCacheKey(fontFamily, fontSize, fontWeight, fontStyle) {
  return `${fontFamily}|${fontSize}|${fontWeight}|${fontStyle}`;
}

// Shared canvas context for measurements (reused across calls)
let sharedCanvas = null;
let sharedContext = null;

function getSharedCanvasContext() {
  if (!sharedCanvas) {
    sharedCanvas = document.createElement('canvas');
    sharedContext = sharedCanvas.getContext('2d');
  }
  return sharedContext;
}

export const charWidth = (() => {
  // LRU caches with size limits
  const MAX_CHAR_CACHE_SIZE = 50000; // Max 50k character entries
  const MAX_TEXT_CACHE_SIZE = 10000; // Max 10k text entries

  // Character width cache: Map<fontKey, LRUCache<char, width>>
  const cachedCharWidth = new Map();

  // Text width cache: Map<fontKey, LRUCache<textKey, width>>
  const cachedTextWidth = new Map();

  // Font key mapping: Map<fontString, fontKey>
  const fontKeyMap = new Map();

  // Reverse mapping: Map<fontKey, Set<fontString>> (for efficient cache clearing)
  const fontKeyToFontStrings = new Map();

  // Get or create font key
  function getFontKey(font) {
    if (fontKeyMap.has(font)) {
      return fontKeyMap.get(font);
    }

    const parsed = parseFontKey(font);
    const fontKey = createFontCacheKey(
      parsed.fontFamily,
      parsed.fontSize,
      parsed.fontWeight,
      parsed.fontStyle
    );

    fontKeyMap.set(font, fontKey);

    if (!fontKeyToFontStrings.has(fontKey)) {
      fontKeyToFontStrings.set(fontKey, new Set());
    }
    fontKeyToFontStrings.get(fontKey).add(font);

    return fontKey;
  }

  // Get or create LRU cache for a font key
  function getCharCache(fontKey) {
    if (!cachedCharWidth.has(fontKey)) {
      cachedCharWidth.set(fontKey, new LRUCache(MAX_CHAR_CACHE_SIZE));
    }
    return cachedCharWidth.get(fontKey);
  }

  function getTextCache(fontKey) {
    if (!cachedTextWidth.has(fontKey)) {
      cachedTextWidth.set(fontKey, new LRUCache(MAX_TEXT_CACHE_SIZE));
    }
    return cachedTextWidth.get(fontKey);
  }

  const calculateText = (text, font, letterSpacing) => {
    const fontKey = getFontKey(font);
    const textCache = getTextCache(fontKey);
    const spacing = Math.round((letterSpacing || 0) / 10) * 10;
    const cacheKey = `${spacing || 0}:${text}`;

    const cached = textCache.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }

    const width = getLineWidth(text, font, letterSpacing);
    textCache.set(cacheKey, width);
    return width;
  };

  // Optimized batch calculation for multiple characters
  // Further optimized: reduce array operations and improve cache lookup
  const calculateBatch = (chars, font) => {
    if (!chars || chars.length === 0) {
      return [];
    }

    const fontKey = getFontKey(font);
    const charCache = getCharCache(fontKey);
    const results = new Array(chars.length);

    // Pre-allocate arrays for better performance
    const missingChars = [];
    const missingIndices = [];
    let missingCount = 0;

    // Single pass: check cache and collect missing characters
    for (let i = 0; i < chars.length; i++) {
      const char = chars[i];
      const cached = charCache.get(char);
      if (cached !== undefined) {
        results[i] = cached;
      } else {
        // Use pre-allocated arrays more efficiently
        if (missingCount >= missingChars.length) {
          missingChars.push(char);
          missingIndices.push(i);
        } else {
          missingChars[missingCount] = char;
          missingIndices[missingCount] = i;
        }
        missingCount++;
      }
    }

    // Batch measure missing characters (only if any missing)
    if (missingCount > 0) {
      const ctx = getSharedCanvasContext();
      ctx.font = font; // Set font once for all measurements

      // Measure all missing characters in a single batch
      // This reduces context switching overhead
      for (let i = 0; i < missingCount; i++) {
        const char = missingChars[i];
        const metrics = ctx.measureText(char);
        const width = Math.floor(metrics.width);
        const idx = missingIndices[i];
        results[idx] = width;

        // Cache immediately for future use
        charCache.set(char, width);
      }
    }

    return results;
  };

  const calculate = (char, font) => {
    const fontKey = getFontKey(font);
    const charCache = getCharCache(fontKey);

    const cached = charCache.get(char);
    if (cached !== undefined) {
      return cached;
    }

    // Use shared canvas context for measurement
    const ctx = getSharedCanvasContext();
    ctx.font = font;
    const metrics = ctx.measureText(char);
    const width = Math.floor(metrics.width);

    charCache.set(char, width);
    return width;
  };

  const getCache = (font) => {
    const fontKey = getFontKey(font);
    const charCache = getCharCache(fontKey);
    // Convert LRU cache to plain object for compatibility
    const result = {};
    for (const [char, width] of charCache.cache) {
      result[char] = width;
    }
    return result;
  };

  const resetCache = (fontFamily) => {
    if (!fontFamily) {
      // Clear all caches
      cachedCharWidth.clear();
      cachedTextWidth.clear();
      fontKeyMap.clear();
      fontKeyToFontStrings.clear();
      return;
    }

    // Clear cache for specific font family (optimized)
    const fontFamilyLower = fontFamily.toLowerCase();
    const keysToDelete = [];

    for (const [fontKey, fontStrings] of fontKeyToFontStrings.entries()) {
      // Check if any font string contains this font family
      let shouldDelete = false;
      for (const fontStr of fontStrings) {
        if (fontStr.toLowerCase().includes(fontFamilyLower)) {
          shouldDelete = true;
          break;
        }
      }

      if (shouldDelete || fontKey.includes(fontFamilyLower)) {
        keysToDelete.push(fontKey);
        // Remove from font key map
        for (const fontStr of fontStrings) {
          fontKeyMap.delete(fontStr);
        }
        fontKeyToFontStrings.delete(fontKey);
      }
    }

    // Delete caches for affected font keys
    for (const fontKey of keysToDelete) {
      cachedCharWidth.delete(fontKey);
      cachedTextWidth.delete(fontKey);
    }
  };

  function clear() {
    resetCache();
  }

  function getStats() {
    let totalChars = 0;
    let totalTexts = 0;

    for (const cache of cachedCharWidth.values()) {
      totalChars += cache.size;
    }
    for (const cache of cachedTextWidth.values()) {
      totalTexts += cache.size;
    }

    return {
      fontKeys: cachedCharWidth.size,
      totalCharEntries: totalChars,
      totalTextEntries: totalTexts,
      maxCharCacheSize: MAX_CHAR_CACHE_SIZE,
      maxTextCacheSize: MAX_TEXT_CACHE_SIZE,
    };
  }

  function debug() {
    const stats = getStats();
    console.log('CharWidth Cache Stats:', stats);
    console.log('Sample char caches:', Array.from(cachedCharWidth.entries()).slice(0, 3));
    console.log('Sample text caches:', Array.from(cachedTextWidth.entries()).slice(0, 3));
  }

  return {
    debug,
    clear,
    calculateText,
    calculateBatch,
    resetCache,
    calculate,
    getCache,
    getStats,
  };
})();

function lcs(str1, str2) {
  const m = str1.length;
  const n = str2.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

  // 构建LCS长度表
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // 从表中回溯以构建LCS
  let lcs = '';
  let i = m,
    j = n;
  while (i > 0 && j > 0) {
    if (str1[i - 1] === str2[j - 1]) {
      lcs = str1[i - 1] + lcs;
      i--;
      j--;
    } else if (dp[i - 1][j] > dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }

  return lcs;
}

const NEW_LINE_REG = /\r\n?/g;
const NEW_LINE_CHAR = '\n';

export function diffText(str1, str2) {
  str1 = str1.replace(NEW_LINE_REG, NEW_LINE_CHAR);
  str2 = str2.replace(NEW_LINE_REG, NEW_LINE_CHAR);
  const lcsStr = lcs(str1, str2);
  const result = [];

  let i = 0,
    j = 0,
    k = 0;
  while (k < lcsStr.length) {
    while (i < str1.length && str1[i] !== lcsStr[k]) {
      result.push({ value: str1[i], removed: true, formerIndex: i });
      i++;
    }
    while (j < str2.length && str2[j] !== lcsStr[k]) {
      result.push({ value: str2[j], added: true, newIndex: j });
      j++;
    }
    result.push({ value: lcsStr[k], formerIndex: i, newIndex: j });
    i++;
    j++;
    k++;
  }

  // 添加剩余的字符
  while (i < str1.length) {
    result.push({ value: str1[i], removed: true, formerIndex: i });
    i++;
  }
  while (j < str2.length) {
    result.push({ value: str2[j], added: true, newIndex: j });
    j++;
  }

  return result;
}

function getLastNewIndex(list) {
  let lastNewIndexItem = null;
  for (let idx = list.length - 1; idx >= 0; idx--) {
    if (list[idx].newIndex !== undefined) {
      lastNewIndexItem = list[idx];
      break;
    }
  }
  return lastNewIndexItem;
}

export function updateHighlightByText(_highlights, textDiff) {
  if (!_highlights) return null;
  const highlights = copy(_highlights);
  const lastDiff = getLastNewIndex(textDiff);
  const isAllRemove = textDiff.every((diff) => diff.removed);
  if (isAllRemove) return [];
  const newTextLength = lastDiff.newIndex;
  const updatedHighlights = highlights
    .map((item) => {
      const highlight = { ...item, selection: [...item.selection] };
      const [originStart, originEnd] = highlight.selection;
      let [selectionStart, selectionEnd] = highlight.selection;
      selectionStart = Math.max(0, selectionStart);

      textDiff.forEach((diff, i) => {
        if (diff.added) {
          const nextWord = getNextChar(textDiff, i);
          if (nextWord) {
            if (nextWord.formerIndex <= selectionStart) {
              selectionStart += 1;
              selectionEnd += 1;
            } else if (
              nextWord.formerIndex > selectionStart &&
              nextWord.formerIndex < selectionEnd
            ) {
              selectionEnd += 1;
            }
          }
        } else if (diff.removed) {
          if (diff.formerIndex === originStart) {
            selectionEnd -= 1;
          } else if (diff.formerIndex > originStart && diff.formerIndex < originEnd) {
            selectionEnd -= 1;
          } else if (diff.formerIndex < originStart) {
            selectionStart -= 1;
            selectionEnd -= 1;
          }
        }
      });

      selectionEnd = Math.min(newTextLength + 1, selectionEnd);
      selectionStart = Math.min(selectionStart, selectionEnd);

      return {
        ...highlight,
        selection: [selectionStart, selectionEnd],
      };
    })
    .filter((highlight) => highlight !== null && highlight.selection[0] !== highlight.selection[1]);
  return updatedHighlights;
}

function getNextChar(textDiff, i) {
  let node = null;
  for (let idx = i; idx < textDiff.length; idx++) {
    const diff = textDiff[idx];
    if (typeof diff.formerIndex !== 'undefined') {
      node = diff;
      break;
    }
  }
  return node;
}
